import { Menu, MenuButtonTrigger, MenuItemGroup } from "@amzn/storm-ui";
import { useCallback, useState, type ReactElement } from "react";
import { useStore } from "zustand";

import { UNIVERSAL_VIEW_CONFIG } from "../../../config/universalView.config";
import { discoveryFilterStore } from "../../../store/filters";
import { DEFAULT_ALL_ITEM } from "../../../types/common";
import type { ContentSubTypeFilterGroupItem } from "../../../types/filters/common";
import { IHubContentType } from "../../../types/ihubContentType";
import type {
    IHubContentProvider,
    InsightDeckTemplateTheme,
} from "../../../types/providers";
import { isAccountAgnosticId } from "../../../utils/permission.utils";
import {
    StyledFilterMenuList,
    StyledFilterMenuListItem,
} from "../commonStyles";

import { TypeFilterOption } from "./TypeFilterOption";

interface TypeFilterButtonProps {
    typeFilterGroups: ContentSubTypeFilterGroupItem[];
}

const TypeFilterButton = (props: TypeFilterButtonProps): ReactElement => {
    const { typeFilterGroups } = props;
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [contentSubType, scopeToLabel, getTypeOptions] = useStore(
        discoveryFilterStore,
        (state) => [
            state.getContentSubType(),
            state.scopeToLabel,
            state.getTypeOptions,
        ],
    );

    const isTypeDisabledForUniversalView = useCallback(
        (scope: string | undefined) => {
            if (isAccountAgnosticId()) {
                return !(
                    UNIVERSAL_VIEW_CONFIG.allowedContentTypes[
                        IHubContentType.INSIGHTS
                    ]?.allowedTemplateTypes?.includes(
                        scope as InsightDeckTemplateTheme,
                    ) ?? false
                );
            }
            return false;
        },
        [],
    );

    const closeMenu = (): void => {
        setIsMenuOpen(false);
    };

    const toggleMenu = (): void => {
        setIsMenuOpen(!isMenuOpen);
    };

    return (
        <Menu
            id={"type-filter-menu"}
            data-testid={"type-filter-menu"}
            onClick={toggleMenu}
            onClose={closeMenu}
            isOpen={isMenuOpen}
            trigger={
                <MenuButtonTrigger data-testid={"type-filter-menu"}>
                    <b>Type:</b>{" "}
                    {contentSubType.scope
                        ? scopeToLabel[contentSubType.scope]
                        : DEFAULT_ALL_ITEM.label}
                </MenuButtonTrigger>
            }
        >
            {typeFilterGroups &&
                typeFilterGroups.length > 0 &&
                (typeFilterGroups.length > 1 ? (
                    typeFilterGroups.map((groupItem) => {
                        return (
                            <MenuItemGroup
                                key={groupItem.label}
                                label={groupItem.label}
                            >
                                <StyledFilterMenuList>
                                    {groupItem?.items
                                        ?.filter(
                                            (leafItem) => !leafItem.disabled,
                                        )
                                        .map((leafItem) => {
                                            const provider =
                                                leafItem.fetchInfo.provider
                                                    .value;
                                            const typeOptions = getTypeOptions(
                                                provider as IHubContentProvider,
                                            );
                                            const shouldDisable =
                                                (!!typeOptions?.length &&
                                                    !typeOptions.includes(
                                                        leafItem.fetchInfo.scope
                                                            .value ?? "",
                                                    )) ||
                                                isTypeDisabledForUniversalView(
                                                    leafItem.fetchInfo.scope
                                                        .value,
                                                );

                                            return (
                                                <StyledFilterMenuListItem>
                                                    <TypeFilterOption
                                                        provider={
                                                            leafItem.fetchInfo
                                                                .provider
                                                        }
                                                        scope={{
                                                            ...leafItem
                                                                .fetchInfo
                                                                .scope,
                                                            disabled:
                                                                shouldDisable,
                                                        }}
                                                        onClick={closeMenu}
                                                    />
                                                </StyledFilterMenuListItem>
                                            );
                                        })}
                                </StyledFilterMenuList>
                            </MenuItemGroup>
                        );
                    })
                ) : (
                    <StyledFilterMenuList>
                        {typeFilterGroups[0]?.items?.map((leafItem) => {
                            return (
                                <StyledFilterMenuListItem>
                                    <TypeFilterOption
                                        provider={leafItem.fetchInfo.provider}
                                        scope={leafItem.fetchInfo.scope}
                                        onClick={closeMenu}
                                    />
                                </StyledFilterMenuListItem>
                            );
                        })}
                    </StyledFilterMenuList>
                ))}
        </Menu>
    );
};

export default TypeFilterButton;
